import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../shared/helpers/icons';
import { ReviewService } from '../../shared/services/review.service';
import { UserDataService } from '../../shared/services/auth/userData.service';
import { ITask } from '../../shared/models/review';
import { EStage } from '../../shared/models/types.enum';
import { EvaluationsReviewModalComponent } from '../components/review-modals/evaluations-review-modal/evaluations-review-modal.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { AssessmentsReviewModalComponent } from "../components/review-modals/assessments-review-modal/assessments-review-modal.component";
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { InterventionReviewModalComponent } from "../components/review-modals/intervention-review-modal/intervention-review-modal.component";
import { MonitoringReviewModalComponent } from '../components/review-modals/monitoring-review-modal/monitoring-review-modal.component';
import { PlansReviewModalComponent } from "../components/review-modals/plans-review-modal/plans-review-modal.component";
import { FormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-my-tasks',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    LucideAngularModule,
    SpinnerComponent,
    PopupMessageComponent,
    EvaluationsReviewModalComponent,
    AssessmentsReviewModalComponent,
    PlansReviewModalComponent,
    InterventionReviewModalComponent,
    MonitoringReviewModalComponent,
    NgxPaginationModule
],
  templateUrl: './my-tasks.component.html',
  styleUrl: './my-tasks.component.css'
})
export class MyTasksComponent implements OnInit {
  private destroy$ = new Subject<void>();

  icons = Icons;
  stageName = EStage;

  searchQuery: string = '';
  taskList: ITask[] = [];
  filteredStageReviewList: ITask[] = [];
  filteredRoarAssignmentList: any[] = [];
  filteredRoarActivityList: any[] = [];
  filteredRoarReviewList: any[] = [];
  mergedTasks: any[] = [];
  selectedTask: ITask | null = null;

  currentBuildingId: string | null = null;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  page = 1;
  itemsPerPage = 10;

  showReviewModal: boolean = false;
  loading: boolean = false;
  sortOrder: boolean = true;

  constructor(
      private router: Router,
    private reviewService: ReviewService,
    private userDataService: UserDataService
  ) { }

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();

    this.loadUserTasks();
  }

  loadUserTasks(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.reviewService.getUserTasks(this.currentBuildingId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const fixTimestamps = (list: any[], dateField: string): any[] =>
            list.map(item => ({
              ...item,
              [dateField]: item[dateField] ? new Date(item[dateField].replace(' ', 'T') + 'Z') : null
            }));
        
          const stageReviewSection = res.stage_review || []
          const roarAssignmentSection = res.roar_assignment || []
          const roarActivitySection = res.roar_activity || []
          const roarReviewSection = res.roar_review || []
  
          this.taskList = res;
          this.filteredStageReviewList = stageReviewSection;
          this.filteredRoarAssignmentList = roarAssignmentSection;
          this.filteredRoarActivityList = roarActivitySection;
          this.filteredRoarReviewList = roarReviewSection;

          this.mergedTasks = [
            ...this.filteredRoarAssignmentList.map(task => ({ ...task, taskType: 'ROAR Assignment Form' })),
            ...this.filteredRoarActivityList.map(task => ({ ...task, taskType: 'ROAR Activity' })),
            ...this.filteredRoarReviewList.map(task => ({ ...task, taskType: 'ROAR Review' })),
            ...this.filteredStageReviewList.map(task => ({ ...task, taskType: 'Stage Review' }))
          ];
          this.loading = false;
        },
        error: (err) => {
          console.error(err);
          this.loading = false;
        }
      });
    }
  }

  sortList(key: keyof typeof this.filteredStageReviewList[0]): void {
    this.sortOrder = !this.sortOrder;
    this.filteredStageReviewList.sort((a, b) => {
      const valueA = a[key]?.toString().toLowerCase() || '';
      const valueB = b[key]?.toString().toLowerCase() || '';

      if (valueA < valueB) {
        return this.sortOrder ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortOrder ? 1 : -1;
      }
      return 0;
    });
  }

  filterAssessCases(): void {
    this.filteredStageReviewList = this.taskList.filter(taskItem => {
      return taskItem.case_id.toLowerCase().includes(this.searchQuery.toLowerCase()) || 
        taskItem.requested_by_name!.toLowerCase().includes(this.searchQuery.toLowerCase());
    });
  }  
  
  goToPage(stage:string, pageName: string, caseId: string, roarId?: string): void {
    this.router.navigate([stage.toLowerCase() + '/' + pageName + '/' + caseId + (roarId ? '/' + roarId : '')]);
  }

  openModal(type: string, clickedCase?: ITask): void {
    if (type === 'review modal') {
      this.showReviewModal = true;
    }
    this.selectedTask = clickedCase ? clickedCase : null;
  }

  closeModal(type: string): void {
    if (type === 'review modal') {
      this.showReviewModal = false;
    }
    this.selectedTask = null;
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.closeModal(actionType);
    if (message.type === 'success') {
      this.messageModal = 'success';
      this.popupMessage = message.message;
      this.loadUserTasks();
    } else if (message.type === 'failed') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
